import React from 'react'
import { IMasterCurricula } from 'infrastructure/api'
import { byStringFieldSort, openTab } from '../../Helpers'
import { IUpdateInfo, migrateVersions } from 'shared/api/mutations'
import {
  Alert,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import {
  getVersionMapping,
  IParsedVersionMapping,
  useCreateVersionMapping,
} from '../TreeActions'
import { useMastersForProduct } from 'shared/api/tree'
import {
  getActiveMigrationVersions,
  IActiveVersionInfo,
} from 'components/masters_management/utility'

export function MigrateSchools({ productId }: { productId: number }) {
  const masterVersions = useMastersForProduct(productId)
  const [toVersion, setToVersion] = React.useState('')
  const [sortedMasters, setSortedMasters] = React.useState<IMasterCurricula[]>(
    [],
  )
  const [activeVersions, setActiveVersions] = React.useState<
    IActiveVersionInfo[]
  >([])
  const [activeVersionsLoading, setActiveVersionsLoading] =
    React.useState(false)
  const [valid, setValid] = React.useState(false)
  const [migrating, setMigrating] = React.useState(false)
  const [message, setMessage] = React.useState('')
  const [mappingMapping, setMappingMapping] = React.useState<{
    [k: string]: IParsedVersionMapping
  }>({})
  const [loadedMappingKeys, setLoadedMappingKeys] = React.useState<string[]>([])
  const [loadingMappingKeys, setLoadingMappingKeys] = React.useState<string[]>(
    [],
  )
  const [notFoundMappingKeys, setNotFoundMappingKeys] = React.useState<
    string[]
  >([])
  const [creatingMappings, setCreatingMappings] = React.useState(false)
  const createVersionMapping = useCreateVersionMapping()

  React.useEffect(() => {
    if (!masterVersions) return
    const sorted = [...masterVersions]
      .sort(byStringFieldSort('createdAt'))
      .reverse()
    setToVersion(sorted[0]?.name)
    setSortedMasters(sorted)
  }, [masterVersions])

  React.useEffect(() => {
    const treeId = sortedMasters[0]?.treeId
    if (!treeId || !sortedMasters.length) return

    let isCurrent = true
    setActiveVersionsLoading(true)

    getActiveMigrationVersions({ treeId, masters: sortedMasters })
      .then((resp) => {
        if (!isCurrent) return
        setActiveVersions(resp)
      })
      .catch(() => {
        if (!isCurrent) return
        setActiveVersions([])
      })
      .finally(() => {
        if (!isCurrent) return
        setActiveVersionsLoading(false)
      })

    return () => {
      isCurrent = false
    }
  }, [sortedMasters])

  React.useEffect(() => {
    const toTree = sortedMasters.find((m) => m.name === toVersion)
    if (!toTree) return

    requiredUpdates({ activeVersions, toTree }).forEach(
      ({ versionToUpdate }) => {
        const fromTree = sortedMasters.find(
          (m) => m.version === versionToUpdate.version,
        )
        if (!fromTree) return

        const mappingKey = `${fromTree.name}:${toTree.name}`
        if (
          mappingMapping[mappingKey] ||
          loadedMappingKeys.includes(mappingKey) ||
          loadingMappingKeys.includes(mappingKey)
        )
          return

        setLoadingMappingKeys((prev) => [...prev, mappingKey])

        getVersionMapping({
          fromTree: { tree_id: fromTree.treeId, version: fromTree.version },
          toTree: { tree_id: toTree.treeId, version: toTree.version },
        })
          .then(({ result, status }) => {
            if (result) {
              setMappingMapping((prev) => ({ ...prev, [mappingKey]: result }))
            } else if (status === 404) {
              setNotFoundMappingKeys((prev) =>
                prev.includes(mappingKey) ? prev : [...prev, mappingKey],
              )
            }
          })
          .finally(() => {
            setLoadedMappingKeys((prev) => [...prev, mappingKey])
            setLoadingMappingKeys((prev) =>
              prev.filter((key) => key !== mappingKey),
            )
          })
      },
    )
  }, [
    activeVersions,
    toVersion,
    sortedMasters,
    mappingMapping,
    loadedMappingKeys,
    loadingMappingKeys,
  ])

  React.useEffect(() => {
    if (!masterVersions) return
    setMessage('')
    const toTree = masterVersions.find((t) => t.name === toVersion)
    if (!toTree) {
      setValid(false)
      return
    }

    if (activeVersionsLoading) {
      setMessage('Loading active versions')
      setValid(false)
      return
    }

    const updates = requiredUpdates({ activeVersions, toTree })
    if (!updates.length) {
      setMessage('Selected version is already active for all curricula')
      setValid(false)
      return
    }

    const hasBackwardMigration = updates.some(({ versionToUpdate }) => {
      const fromTree = masterVersions.find(
        (tree) => tree.version === versionToUpdate.version,
      )
      if (!fromTree) return false
      return toTree.createdAt.localeCompare(fromTree.createdAt) < 0
    })

    if (hasBackwardMigration) {
      setMessage('Migrations can go only forward, not backwards')
      setValid(false)
      return
    }

    const requiredMappingKeys = updates
      .map(({ versionToUpdate }) => {
        const fromTree = masterVersions.find(
          (tree) => tree.version === versionToUpdate.version,
        )
        return fromTree ? `${fromTree.name}:${toTree.name}` : undefined
      })
      .filter((key): key is string => !!key)

    const hasMappingsLoading = requiredMappingKeys.some((mappingKey) => {
      return loadingMappingKeys.includes(mappingKey)
    })

    if (hasMappingsLoading) {
      setMessage('Loading mappings')
      setValid(false)
      return
    }

    const missingMappings = updates.filter(({ versionToUpdate }) => {
      const fromTree = masterVersions.find(
        (tree) => tree.version === versionToUpdate.version,
      )
      if (!fromTree) return true
      const mappingKey = `${fromTree.name}:${toTree.name}`
      if (!loadedMappingKeys.includes(mappingKey)) {
        return false
      }
      const mapping = mappingMapping[mappingKey]
      return !mapping?.matched.length
    })

    if (missingMappings.length) {
      const allAre404 = missingMappings.every(({ versionToUpdate }) => {
        const fromTree = masterVersions.find(
          (tree) => tree.version === versionToUpdate.version,
        )
        if (!fromTree) return false
        return notFoundMappingKeys.includes(`${fromTree.name}:${toTree.name}`)
      })

      setMessage(
        allAre404
          ? `No mappings exist yet for versions: ${missingMappings
              .map(({ versionToUpdate }) => versionToUpdate.name)
              .join(', ')}. Click "Create Mappings" to generate them.`
          : `Mapping missing for schools/districts currently on versions: ${missingMappings
              .map(({ versionToUpdate }) => versionToUpdate.name)
              .join(', ')}`,
      )
      setValid(true)
      return
    }

    const incompleteMappings = updates.filter(({ versionToUpdate }) => {
      const fromTree = masterVersions.find(
        (tree) => tree.version === versionToUpdate.version,
      )
      if (!fromTree) return true
      return !!mappingMapping[`${fromTree.name}:${toTree.name}`]?.missing.length
    })

    if (incompleteMappings.length) {
      const missingCount = incompleteMappings.reduce(
        (count, { versionToUpdate }) => {
          const fromTree = masterVersions.find(
            (tree) => tree.version === versionToUpdate.version,
          )
          if (!fromTree) return count

          return (
            count +
            (mappingMapping[`${fromTree.name}:${toTree.name}`]?.missing
              .length ?? 0)
          )
        },
        0,
      )

      setMessage(
        `${missingCount} ${pluralize('mapping', missingCount)} still need review before migration`,
      )
      setValid(true)
      return
    }

    setValid(true)
  }, [
    toVersion,
    masterVersions,
    mappingMapping,
    loadedMappingKeys,
    loadingMappingKeys,
    activeVersions,
    activeVersionsLoading,
    notFoundMappingKeys,
  ])

  function versions() {
    if (!activeVersions.length) {
      return sortedMasters.map((m) => m.name)
    }

    const oldestActiveIndex = activeVersions.reduce(
      (oldestIndex, { master }) => {
        const activeIndex = sortedMasters.findIndex(
          (sortedMaster) => sortedMaster.version === master.version,
        )
        return Math.max(oldestIndex, activeIndex)
      },
      0,
    )

    return sortedMasters.slice(0, oldestActiveIndex + 1).map((m) => m.name)
  }

  function doMigration() {
    if (!masterVersions) return
    const toTree = masterVersions.find((t) => t.name === toVersion)
    if (!toTree || !valid) return

    const updates = requiredUpdates({ activeVersions, toTree })
    if (!updates.length) return

    setMigrating(true)
    migrateVersions(updates)
      .then(() => {
        setMessage('Request sent')
      })
      .catch(() => {
        setMessage('Request error')
      })
      .finally(() => {
        setMigrating(false)
      })
  }

  async function handleCreateMappings() {
    if (!masterVersions) return
    const toTree = masterVersions.find((t) => t.name === toVersion)
    if (!toTree) return

    const relevantKeys = notFoundMappingKeys.filter((key) =>
      key.endsWith(`:${toTree.name}`),
    )
    if (!relevantKeys.length) return

    setCreatingMappings(true)
    try {
      for (const mappingKey of relevantKeys) {
        const [fromName] = mappingKey.split(':')
        const fromTree = masterVersions.find((m) => m.name === fromName)
        if (!fromTree) continue

        await createVersionMapping({
          fromTree: { tree_id: fromTree.treeId, version: fromTree.version },
          toTree: { tree_id: toTree.treeId, version: toTree.version },
        })
      }

      // Clear state to trigger re-fetch. The useWithErrorNotification hook
      // shows a dialog on API errors but always resolves (never throws),
      // so any failed mappings will 404 again on re-fetch and reappear.
      setNotFoundMappingKeys((prev) =>
        prev.filter((key) => !relevantKeys.includes(key)),
      )
      setMessage('Mappings created successfully. Re-fetching...')
      setMappingMapping({})
      setLoadedMappingKeys([])
      setLoadingMappingKeys([])
    } catch {
      setMessage('Failed to create mappings. Please try again.')
    } finally {
      setCreatingMappings(false)
    }
  }

  const goToMappingTarget = getGoToMappingTarget({
    activeVersions,
    masterVersions,
    mappingMapping,
    toVersion,
  })

  function goToSelectedMapping() {
    if (!goToMappingTarget) return

    openTab(
      `/version_mapping/from/${goToMappingTarget.fromTree.treeId}:${goToMappingTarget.fromTree.version}/to/${goToMappingTarget.toTree.treeId}:${goToMappingTarget.toTree.version}?rf=mismatchedPaths,mismatchedTitles`,
    )
  }

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 720,
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={3}>
        <Stack spacing={1.5}>
          <Typography variant="h6">Active Versions</Typography>
          <List
            disablePadding
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: 'grey.50',
            }}
          >
            {activeVersions.map(({ master, schoolCount, districtCount }) => (
              <ListItem
                key={master.version}
                divider
                sx={{
                  px: 2,
                  py: 1.5,
                  alignItems: 'center',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <ListItemText
                  primary={master.name}
                  primaryTypographyProps={{ fontWeight: 600 }}
                  secondary={`${schoolCount} ${pluralize('school', schoolCount)}, ${districtCount} ${pluralize('district', districtCount)}`}
                />
              </ListItem>
            ))}
          </List>
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="h6">Migrate To Version</Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the final version for all active schools and districts.
          </Typography>
          <VersionSelect
            selected={toVersion}
            versions={versions()}
            onSelect={setToVersion}
          />
        </Stack>

        <Stack spacing={1.5}>
          <Typography variant="h6">Status</Typography>
          <Stack spacing={1.5}>
            {activeVersions.length > 1 && (
              <Alert severity="warning" variant="outlined">
                Vanity URLs do not support different active versions across
                schools and districts. Choose a final version to normalize all
                active curricula.
              </Alert>
            )}
            {migrating && (
              <Alert severity="info" variant="outlined">
                Please wait a few minutes, then refresh to see updated migration
                state.
              </Alert>
            )}
            <Alert
              severity={messageSeverity({ message, valid, migrating })}
              variant="outlined"
            >
              {message ||
                'Review the active versions and choose a target version.'}
            </Alert>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          {notFoundMappingKeys.some((key) => key.endsWith(`:${toVersion}`)) && (
            <Button
              variant="outlined"
              color="warning"
              disabled={creatingMappings}
              onClick={handleCreateMappings}
            >
              {creatingMappings ? 'Creating Mappings...' : 'Create Mappings'}
            </Button>
          )}
          {goToMappingTarget && (
            <Button variant="outlined" onClick={goToSelectedMapping}>
              Go To Mapping
            </Button>
          )}
          <Button
            variant="text"
            onClick={() => {
              setMappingMapping({})
              setLoadedMappingKeys([])
              setLoadingMappingKeys([])
              setNotFoundMappingKeys([])
            }}
          >
            Refresh mappings
          </Button>
          <Button
            variant="contained"
            disabled={!valid || migrating}
            onClick={doMigration}
          >
            Run migration
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

function messageSeverity({
  message,
  valid,
  migrating,
}: {
  message: string
  valid: boolean
  migrating: boolean
}): 'success' | 'info' | 'warning' | 'error' {
  if (migrating) return 'info'
  if (!message) return valid ? 'success' : 'info'
  if (message.includes('Request sent')) return 'success'
  if (message.includes('Request error')) return 'error'
  if (message.includes('complete mapping')) return 'error'
  if (message.includes('not backwards')) return 'warning'
  if (message.includes('already active')) return 'info'
  if (message.includes('Loading active versions')) return 'info'
  return valid ? 'warning' : 'error'
}

function pluralize(word: string, count: number) {
  return count === 1 ? word : `${word}s`
}

function requiredUpdates({
  activeVersions,
  toTree,
}: {
  activeVersions: IActiveVersionInfo[]
  toTree?: IMasterCurricula
}): IUpdateInfo[] {
  if (!toTree) return []

  return activeVersions.reduce<IUpdateInfo[]>((acc, { master }) => {
    if (master.version === toTree.version) {
      return acc
    }

    acc.push({
      destinationVersion: {
        treeId: toTree.treeId,
        version: toTree.version,
        name: toTree.name,
        title: toTree.title,
      },
      versionToUpdate: {
        name: master.name,
        version: master.version,
      },
    })
    return acc
  }, [])
}

function getGoToMappingTarget({
  activeVersions,
  masterVersions,
  mappingMapping,
  toVersion,
}: {
  activeVersions: IActiveVersionInfo[]
  masterVersions: IMasterCurricula[] | undefined
  mappingMapping: Record<string, IParsedVersionMapping>
  toVersion: string
}) {
  if (!masterVersions?.length) return undefined

  const toTree = masterVersions.find((tree) => tree.name === toVersion)
  if (!toTree) return undefined

  const updates = requiredUpdates({ activeVersions, toTree })

  return updates
    .map(({ versionToUpdate }) => {
      const fromTree = masterVersions.find(
        (tree) => tree.version === versionToUpdate.version,
      )
      if (!fromTree) return undefined

      const mapping = mappingMapping[`${fromTree.name}:${toTree.name}`]
      if (!mapping?.missing.length) return undefined

      return { fromTree, toTree }
    })
    .find((target) => !!target)
}

function VersionSelect({
  selected,
  versions,
  onSelect,
}: {
  selected: string
  versions: string[]
  onSelect: (v: string) => void
}) {
  return (
    <Box>
      <Select<string>
        variant={'outlined'}
        value={selected}
        onChange={(e) => onSelect(e.target.value)}
        size={'small'}
        sx={{ minWidth: 220, backgroundColor: 'background.paper' }}
      >
        {versions.map((v) => (
          <MenuItem key={v} value={v}>
            {v}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}
